// src/pages/sow-template/SowTemplatePage.jsx
//
// SOW Template builder. State/handlers live in useSowTemplate(); this
// file is composition only -- see TemplatesPanel.jsx, SectionsPanel.jsx,
// SectionEditorPanel.jsx, and the four modals for the rest. Deliberately
// kept as one page (list + builder both always visible), per explicit
// direction -- only the styling and a couple of interaction details
// (upload form -> modal, window.prompt -> AddSectionModal) changed.

import { Download, Eye, RefreshCw, Save, Trash2 } from "lucide-react";
import { Alert, Button, PageHeader, Select, SurfaceCard } from "../../shared/ui";
import { useSowTemplate } from "./useSowTemplate";
import TemplatesPanel from "./TemplatesPanel";
import SectionsPanel from "./SectionsPanel";
import SectionEditorPanel from "./SectionEditorPanel";
import UploadTemplateModal from "./UploadTemplateModal";
import AddSectionModal from "./AddSectionModal";
import PlaceholderModal from "./PlaceholderModal";
import TemplatePreviewModal from "./TemplatePreviewModal";

export default function SowTemplatePage() {
  const h = useSowTemplate();

  const sortedSections = [...(h.tpl?.sections_json || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div>
      <PageHeader
        title="SOW Template Builder"
        subtitle="Upload a DOCX or build sections, manage placeholders, then save."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="secondary" size="sm" onClick={h.refreshList} disabled={h.busy}>
              <RefreshCw size={14} /> Refresh
            </Button>
            <Button variant="secondary" size="sm" onClick={() => h.setPreviewOpen(true)} disabled={!h.tpl}>
              <Eye size={14} /> Preview
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={h.downloadTemplateDocx}
              disabled={!h.tpl?.id || h.busy}
            >
              <Download size={14} /> Download DOCX
            </Button>
            <Button
              size="sm"
              onClick={h.saveTemplate}
              disabled={!h.tpl || h.busy || (h.tpl?.id ? !h.canManageTemplate(h.tpl) : !h.canCreateAnything)}
            >
              <Save size={14} /> Save Template
            </Button>
            <Button
              variant="danger"
              size="sm"
              onClick={h.deleteTemplate}
              disabled={!h.tpl?.id || h.busy || !h.canManageTemplate(h.tpl)}
            >
              <Trash2 size={14} /> Delete
            </Button>
          </div>
        }
      />

      {h.msg.text && (
        <Alert variant={h.msg.type === "error" ? "danger" : "success"} className="mb-4">
          {h.msg.text}
        </Alert>
      )}

      <TemplatesPanel
        q={h.q}
        onQChange={h.setQ}
        contractType={h.contractType}
        onContractTypeChange={h.setContractType}
        scopeFilter={h.scopeFilter}
        onScopeFilterChange={h.setScopeFilter}
        rows={h.rows}
        selectedId={h.selectedId}
        onOpen={h.loadTemplate}
        onNew={h.startNewTemplate}
        onOpenUpload={() => h.setUploadModalOpen(true)}
        loading={h.busy}
        canCreate={h.canCreateAnything}
        permissionsLoading={h.permissionsLoading}
      />

      {h.tpl && !h.tpl.id && (
        <SurfaceCard className="mb-4">
          <h2 className="text-base font-semibold text-ink mb-3">Scope</h2>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="w-56">
              <label className="block text-xs font-medium text-muted mb-1">Visibility</label>
              <Select
                options={[
                  ...(h.canManageGlobal ? [{ value: "global", label: "Global (platform-wide baseline)" }] : []),
                  ...(h.canManageOrgWide ? [{ value: "org", label: "Org-wide (everyone in your org)" }] : []),
                  ...(h.manageableAccountIds.length ? [{ value: "account", label: "One customer account" }] : []),
                ]}
                value={h.scope}
                onChange={(e) => h.setScope(e.target.value)}
              />
            </div>
            {h.scope === "account" && (
              <div className="w-64">
                <label className="block text-xs font-medium text-muted mb-1">Account</label>
                <Select
                  options={[
                    { value: "", label: "Choose an account…" },
                    ...h.selectableAccounts.map((a) => ({ value: String(a.account_id), label: a.name })),
                  ]}
                  value={h.scopeAccountId}
                  onChange={(e) => h.setScopeAccountId(e.target.value)}
                />
              </div>
            )}
          </div>
        </SurfaceCard>
      )}

      <SectionsPanel
        tpl={h.tpl}
        onFieldChange={h.setTemplateField}
        sections={sortedSections}
        selectedSectionKey={h.selectedSectionKey}
        onSelectSection={h.setSelectedSectionKey}
        onMoveSection={h.moveSection}
        onDeleteSection={h.deleteSection}
        onAddSection={() => h.setAddSectionModalOpen(true)}
        unregisteredPlaceholders={h.unregisteredUsedPlaceholders}
        onRegisterPlaceholder={h.openAddPlaceholder}
        allPlaceholders={h.allPlaceholders}
        onInsertPlaceholder={h.insertPlaceholder}
        onOpenAddPlaceholder={() => h.openAddPlaceholder()}
      />

      <SectionEditorPanel tpl={h.tpl} section={h.selectedSection} onFieldChange={h.setSectionField} editorRef={h.editorRef} />

      <UploadTemplateModal
        open={h.uploadModalOpen}
        onClose={() => h.setUploadModalOpen(false)}
        name={h.uploadName}
        onNameChange={h.setUploadName}
        version={h.uploadVersion}
        onVersionChange={h.setUploadVersion}
        contractType={h.uploadContractType}
        onContractTypeChange={h.setUploadContractType}
        file={h.docxFile}
        onFileChange={h.setDocxFile}
        onUpload={h.uploadDocx}
        busy={h.busy}
        scope={h.scope}
        onScopeChange={h.setScope}
        scopeOptions={[
          ...(h.canManageGlobal ? [{ value: "global", label: "Global (platform-wide baseline)" }] : []),
          ...(h.canManageOrgWide ? [{ value: "org", label: "Org-wide (everyone in your org)" }] : []),
          ...(h.manageableAccountIds.length ? [{ value: "account", label: "One customer account" }] : []),
        ]}
        scopeAccountId={h.scopeAccountId}
        onScopeAccountIdChange={h.setScopeAccountId}
        accountOptions={h.selectableAccounts.map((a) => ({ value: String(a.account_id), label: a.name }))}
      />

      <AddSectionModal
        open={h.addSectionModalOpen}
        onClose={() => h.setAddSectionModalOpen(false)}
        onAdd={h.addSection}
      />

      <PlaceholderModal
        open={h.placeholderModalOpen}
        onClose={() => h.setPlaceholderModalOpen(false)}
        editingKey={h.phEditKey}
        phKey={h.phKey}
        onKeyChange={h.setPhKey}
        phLabel={h.phLabel}
        onLabelChange={h.setPhLabel}
        phType={h.phType}
        onTypeChange={h.setPhType}
        phRequired={h.phRequired}
        onRequiredChange={h.setPhRequired}
        onSave={h.savePlaceholder}
      />

      <TemplatePreviewModal
        open={h.previewOpen}
        onClose={() => h.setPreviewOpen(false)}
        templateName={h.tpl?.name}
        html={h.previewHtml}
      />
    </div>
  );
}
